import React, { useEffect, useState } from "react"
import { Box, Divider, Button, Grid, makeStyles, FormHelperText } from "@material-ui/core"
import { toId } from "@utils/data"
import { hexToRgb, getContrast, generateShade } from "@utils/useThemingUtils"
import { RESPONSE_SET_DISPLAY_AS } from "@data/enums"
import { checkLogic } from "@services/jobStepLogic"
import { ProcessCollapse } from "./ProcessCollapse"
import { Icon } from "../Icon/Icon"

const useRowStyles = makeStyles((theme) => ({
  row: {
    marginBottom: theme.spacing(0.5),
    "&:last-child": {
      marginBottom: theme.spacing(-0.5)
    }
  }
}))

const useStyles = makeStyles((theme) => ({
  selected: {
    color: ({ customStyle }) =>
      customStyle
        ? getContrast(customStyle.background, theme.palette.common.black, 10.5)
          ? theme.palette.text.primary
          : theme.palette.text.white
        : theme.palette.text.white,
    background: ({ customStyle }) => (customStyle ? customStyle.background : theme.palette.info.main),
    boxShadow: "none",
    transition: "all 0.2s",
    "&:hover": {
      boxShadow: "none",
      transition: "all 0.2s ease",
      backgroundColor: ({ customStyle }) =>
        customStyle ? generateShade(customStyle.background, 600) : generateShade(theme.palette.info.main, 600) // Add contrast for hover
    },
    "&:focus": {
      transition: "all 0.2s ease",
      boxShadow: ({ customStyle }) =>
        customStyle
          ? `0px 0px 0px 3px rgba(${hexToRgb(customStyle.background)},0.5)`
          : `0px 0px 0px 3px rgba(${theme.palette.info.mainRgb},0.5)`,
      outline: `2px solid transparent`
    }
  }
}))

const MultipleChoiceButtonsStep = ({
  step,
  expand,
  responses,
  value,
  buttonClass,
  onLogicResult,
  onClick,
  disabled,
  classes,
  collapseTimeout = "auto",
  responseSetStyles
}) => {
  const rowClasses = useRowStyles()
  const [hasRequirements, setHasRequirements] = useState(false)
  const [requirementsMessages, setRequirementsMessages] = useState([])

  useEffect(() => {
    const logicResult = checkLogic(step, value)
    if (logicResult.hasRequirements) {
      setHasRequirements(true)
      setRequirementsMessages([...logicResult.messages])
    } else if (hasRequirements) {
      setHasRequirements(false)
      setRequirementsMessages([])
    }
    onLogicResult && onLogicResult(logicResult)
  }, [hasRequirements, onLogicResult, step, value])

  const handleClick = (...args) => {
    onClick && onClick(...args)
  }

  if (!responses || !responses.length) {
    return null
  }

  const maxResponses = 10
  const numberRows = Math.ceil(responses.length / maxResponses)
  const itemsPerRow = Math.ceil(responses.length / numberRows)

  let rows = []

  if (responses.length > maxResponses) {
    for (let i = 0; i < responses.length; i += itemsPerRow) {
      const chunk = responses.slice(i, i + itemsPerRow)
      rows.push(chunk)
    }
  } else {
    rows = [responses]
  }

  return (
    <ProcessCollapse in={expand} timeout={collapseTimeout}>
      <Divider className={classes.divider} />
      <Box py={1} px={2}>
        {rows.map((row, index) => (
          <Grid key={index} container spacing={1} className={rowClasses.row}>
            {row.map((response) => (
              <MultipleChoiceButton
                key={toId(response)}
                id={response.id}
                answer={response.text}
                setResponseValue={response.value}
                value={value}
                onClick={handleClick}
                className={buttonClass}
                disabled={disabled}
                responseSetStyles={responseSetStyles}
                showIcon={step?.responseSet?.displayAs === RESPONSE_SET_DISPLAY_AS.ICON}
                xs
              />
            ))}
          </Grid>
        ))}
      </Box>
      {requirementsMessages?.length > 0 && (
        <Box px={2} mb={1}>
          <FormHelperText error id={`${toId(step)}-error`} component="div">
            {[...requirementsMessages].filter(Boolean).map((message, index) => (
              <Box key={index}>{message}</Box>
            ))}
          </FormHelperText>
        </Box>
      )}
    </ProcessCollapse>
  )
}

const MultipleChoiceButton = ({
  id,
  answer,
  setResponseValue,
  value,
  onClick,
  className,
  disabled: disabledInput,
  showIcon = false,
  responseSetStyles,
  ...rest
}) => {
  const selected = !!value && value.toLowerCase() === setResponseValue.toLowerCase()
  const disabled = !!value && !selected

  const customStyle = responseSetStyles?.find((o) => o.response === id)

  const buttonClasses = useStyles({ customStyle })

  const handleClick = (event) => {
    onClick && onClick(event, setResponseValue)
  }
  const isSelected = selected ? `${className} ${buttonClasses.selected}` : className

  const renderDisplayAsIcon = (text) => {
    const iconName = text.replace("_", "-")

    return <Icon name={iconName} />
  }

  return (
    <Grid item {...rest}>
      <Button
        color="inherit"
        variant={disabled ? "text" : "contained"}
        fullWidth
        onClick={handleClick}
        className={isSelected}
        disabled={disabled || disabledInput}
      >
        {showIcon ? <>{renderDisplayAsIcon(answer)}</> : <>{answer}</>}
      </Button>
    </Grid>
  )
}

export { MultipleChoiceButtonsStep, MultipleChoiceButton }
